import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  TrendingUp,
  Download,
  ChevronDown,
  ChevronRight,
  Maximize2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  X,
  Image,
  Loader2,
  Camera,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import { getDownloadUrlOrOpen } from '@/services/documentsService';
import { getClientGarments } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const toKg = (g, cant) => ((cant * (g || 0)) / 1000).toFixed(2);
const fmtNum = (n) => Number(n).toFixed(2);
const fmtDT = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const hasDiferencias = (tx) =>
  (tx.items || []).some(
    (i) => i.cantidad_confirmada != null && i.cantidad_confirmada !== i.cantidad_alistada,
  );

// ────────────── ───────────────────────────────────────────────
// Kardex Table (used by InformePanel)
// ─────────────────────────────────────────────────────────────
const KardexTableInforme = ({ prendas, txs, data, fullscreen }) => {
  const fmtCol = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
  };
  return (
    <div className={`overflow-x-auto ${fullscreen ? '' : 'max-h-80'} overflow-y-auto`}>
      <table className="text-xs border-collapse min-w-max">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <th className="text-left px-3 py-2 border border-gray-200 bg-gray-50 min-w-[130px] sticky left-0 z-20">Prenda</th>
            {txs.map((tx) => (
              <th key={tx._id} className="px-2 py-1 border border-gray-200 text-center min-w-[70px]">
                <div className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold mb-0.5 ${tx.tipo === 'Recoleccion' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                  {tx.tipo === 'Recoleccion' ? 'R' : 'E'}
                </div>
                <div className="font-semibold text-[10px] text-gray-700 leading-tight">{tx.consecutivo}</div>
                <div className="text-gray-400 text-[9px]">{fmtCol(tx.fecha_recoleccion || tx.fecha_entrega || tx.fecha_creacion)}</div>
              </th>
            ))}
            <th className="px-3 py-2 border border-gray-200 bg-gray-50 text-center font-bold sticky right-0 z-20 min-w-[70px]">Saldo Final</th>
          </tr>
        </thead>
        <tbody>
          {prendas.map((p) => {
            const lastTx = txs[txs.length - 1];
            const saldoFinal = lastTx ? (data[p.id]?.[lastTx._id]?.balance ?? 0) : 0;
            return (
              <tr key={p.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 border border-gray-200 font-medium text-gray-800 sticky left-0 bg-white z-10">{p.nombre}</td>
                {txs.map((tx) => {
                  const cell = data[p.id]?.[tx._id];
                  if (!cell || cell.mov === 0) {
                    return <td key={tx._id} className="px-2 py-1 border border-gray-100 text-center text-gray-300">-</td>;
                  }
                  const isPos = cell.mov > 0;
                  return (
                    <td key={tx._id} className={`px-2 py-1 border border-gray-100 text-center ${isPos ? 'bg-blue-50' : 'bg-green-50'}`}>
                      <div className={`font-bold text-xs ${isPos ? 'text-blue-700' : 'text-green-700'}`}>{isPos ? `+${cell.mov}` : cell.mov}</div>
                      <div className="text-gray-500 text-[10px]">({cell.balance})</div>
                    </td>
                  );
                })}
                <td className={`px-3 py-2 border border-gray-200 text-center font-bold sticky right-0 z-10 ${saldoFinal < 0 ? 'bg-red-50 text-red-600' : saldoFinal > 0 ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-400'}`}>
                  {saldoFinal > 0 ? `+${saldoFinal}` : saldoFinal || '-'}
                </td>
              </tr>
            );
          })}
          {/* Fila de totales */}
          <tr className="border-t-2 border-gray-300 bg-gray-100 sticky bottom-0 z-10">
            <td className="px-3 py-2 border border-gray-200 font-bold text-gray-700 text-xs uppercase sticky left-0 bg-gray-100 z-20">Total</td>
            {txs.map((tx) => {
              const total = prendas.reduce((sum, p) => sum + Math.abs(data[p.id]?.[tx._id]?.mov ?? 0), 0);
              const isRecol = tx.tipo === 'Recoleccion';
              return (
                <td key={tx._id} className={`px-2 py-2 border border-gray-200 text-center font-bold text-xs ${isRecol ? 'text-blue-700' : 'text-green-700'}`}>
                  {total > 0 ? (isRecol ? `+${total}` : `-${total}`) : '-'}
                </td>
              );
            })}
            {(() => {
              const totalSaldo = prendas.reduce((sum, p) => {
                const lastTx = txs[txs.length - 1];
                return sum + (lastTx ? (data[p.id]?.[lastTx._id]?.balance ?? 0) : 0);
              }, 0);
              return (
                <td className={`px-3 py-2 border border-gray-200 text-center font-bold text-xs sticky right-0 z-20 bg-gray-100 ${totalSaldo < 0 ? 'text-red-600' : totalSaldo > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                  {totalSaldo > 0 ? `+${totalSaldo}` : totalSaldo || '-'}
                </td>
              );
            })()}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Detalle Table (used by InformePanel)
// ─────────────────────────────────────────────────────────────
const DetalleTableInforme = ({ transactions, excludedIds = new Set(), onToggleExclude }) => {
  const [expanded, setExpanded] = useState(new Set());
  const [evidenciaUrls, setEvidenciaUrls] = useState({});
  const [loadingEvidencia, setLoadingEvidencia] = useState({});
  const sorted = useMemo(
    () => [...transactions].sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion)),
    [transactions],
  );
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadEvidencia = async (tx) => {
    if (tx.tipo !== 'Recoleccion') return;
    if (!Array.isArray(tx.evidencias) || tx.evidencias.length === 0) return;
    if (evidenciaUrls[tx._id] || loadingEvidencia[tx._id]) return;
    setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: true }));
    try {
      const urls = await Promise.all(
        tx.evidencias.map((evId) =>
          getDownloadUrlOrOpen({ id: evId, openInNewTab: false }).catch(() => null)
        )
      );
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: urls.filter(Boolean) }));
    } catch {
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: [] }));
    } finally {
      setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: false }));
    }
  };

  useEffect(() => {
    sorted.forEach((tx) => {
      if (expanded.has(tx._id)) loadEvidencia(tx);
    });
  }, [expanded]);

  return (
    <table className="w-full text-sm min-w-[700px]">
      <thead>
        <tr className="border-b bg-gray-50 text-xs text-gray-500 uppercase">
          <th className="w-8" />
          <th className="w-10 px-2 py-2.5 text-center font-medium" title="Excluir del reporte">Excluir</th>
          <th className="text-left px-3 py-2.5 font-medium">Tipo</th>
          <th className="text-left px-3 py-2.5 font-medium">Tipo Entrega</th>
          <th className="text-left px-3 py-2.5 font-medium">Consecutivo</th>
          <th className="text-left px-3 py-2.5 font-medium">Fecha</th>
          <th className="text-left px-3 py-2.5 font-medium">Conductor</th>
          <th className="text-left px-3 py-2.5 font-medium">Receptor</th>
          <th className="text-right px-3 py-2.5 font-medium">Total Alistado</th>
          <th className="text-right px-3 py-2.5 font-medium">Total Confirmado</th>
          <th className="text-right px-3 py-2.5 font-medium">Peso (KG)</th>
          <th className="text-center px-3 py-2.5 font-medium">Diferencias</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((tx) => {
          const isRecol = tx.tipo === 'Recoleccion';
          const isExpanded = expanded.has(tx._id);
          const items = tx.items || [];
          const conDif = hasDiferencias(tx);
          const totalAlistado = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
          const totalConfirmado = items.reduce((s, i) => s + (i.cantidad_confirmada ?? i.cantidad_alistada ?? 0), 0);
          const totalPeso = items.reduce((s, i) => {
            const cant = i.cantidad_confirmada ?? i.cantidad_alistada ?? 0;
            return s + (cant * (i.peso || 0) / 1000);
          }, 0);
          const fecha = isRecol ? tx.fecha_recoleccion : tx.fecha_entrega;
          const isExcluded = excludedIds.has(tx._id);
          return [
            <tr
              key={tx._id}
              className={`border-b transition-colors ${isExcluded ? 'opacity-40 bg-gray-100' : conDif ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
            >
              <td className="pl-3 py-2.5 cursor-pointer" onClick={() => toggle(tx._id)}>
                {isExpanded
                  ? <ChevronDown className="h-4 w-4 text-gray-400" />
                  : <ChevronRight className="h-4 w-4 text-gray-400" />}
              </td>
              <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isExcluded}
                  onChange={() => onToggleExclude?.(tx._id)}
                  title={isExcluded ? 'Incluir en el reporte' : 'Excluir del reporte'}
                  className="h-4 w-4 cursor-pointer accent-red-500"
                />
              </td>
              <td className="px-3 py-2.5">
                <Badge className={`text-xs font-medium ${isRecol ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                  {isRecol ? 'Recolección' : 'Entrega'}
                </Badge>
              </td>
              <td className="px-3 py-2.5 text-gray-500 text-xs capitalize">
                {(isRecol ? tx.tipoRecoleccion : tx.tipoEntrega) || '-'}
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-gray-800">{tx.consecutivo}</span>
                  {isRecol && Array.isArray(tx.evidencias) && tx.evidencias.length > 0 && (
                    <span title={`${tx.evidencias.length} evidencia(s)`} className="inline-flex items-center gap-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                      <Camera className="h-3 w-3" />
                      {tx.evidencias.length}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2.5 text-gray-600">{fmtDT(fecha || tx.fecha_creacion)}</td>
              <td className="px-3 py-2.5 text-gray-600">{tx.conductor?.nombre || '-'}</td>
              <td className="px-3 py-2.5 text-gray-600">{tx.recibe?.nombre || '-'}</td>
              <td className="px-3 py-2.5 text-right font-bold text-gray-800">{totalAlistado}</td>
              <td className="px-3 py-2.5 text-right font-bold text-gray-800">{totalConfirmado}</td>
              <td className="px-3 py-2.5 text-right font-semibold text-gray-700">{fmtNum(totalPeso)}</td>
              <td className="px-3 py-2.5 text-center">
                {conDif ? (
                  <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-semibold">
                    <AlertTriangle className="h-3 w-3" /> Con diferencias
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                    <CheckCircle className="h-3.5 w-3.5" /> Conforme
                  </span>
                )}
              </td>
            </tr>,
            isExpanded && (
              <tr key={`${tx._id}-detail`} className={`border-b ${isExcluded ? 'opacity-40 bg-gray-100' : conDif ? 'bg-amber-50/60' : 'bg-gray-50'}`}>
                <td colSpan={12} className="px-6 py-3">
                  {conDif && (
                    <div className="flex items-center gap-2 mb-3 text-amber-700 bg-amber-100 border border-amber-200 rounded-lg px-3 py-2 text-xs font-medium">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      Este registro tiene ítems con diferencias entre la cantidad alistada y la cantidad confirmada.
                    </div>
                  )}
                  <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Detalle de Items:</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b">
                        <th className="text-left py-1 font-medium">Prenda</th>
                        <th className="text-right py-1 font-medium text-gray-400">Cant. Alistada</th>
                        <th className="text-right py-1 font-medium">Cant. Confirmada</th>
                        <th className="text-right py-1 font-medium text-amber-600">Diferencia</th>
                        <th className="text-right py-1 font-medium">Peso Unit. (g)</th>
                        <th className="text-right py-1 font-medium">Peso Total (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => {
                        const cantAlistada = item.cantidad_alistada || 0;
                        const cantConfirmada = item.cantidad_confirmada ?? cantAlistada;
                        const diff = cantConfirmada - cantAlistada;
                        const hasDiff = item.cantidad_confirmada != null && item.cantidad_confirmada !== item.cantidad_alistada;
                        const pesoTotal = cantConfirmada * (item.peso || 0) / 1000;
                        return (
                          <tr key={idx} className={`border-b border-gray-100 ${hasDiff ? 'bg-amber-50' : ''}`}>
                            <td className={`py-1.5 font-medium ${hasDiff ? 'text-amber-800' : 'text-gray-800'}`}>
                              {item.nombre}
                              {hasDiff && <AlertTriangle className="h-3 w-3 text-amber-500 inline ml-1" />}
                            </td>
                            <td className="py-1.5 text-right text-gray-400">{cantAlistada}</td>
                            <td className={`py-1.5 text-right font-bold ${hasDiff ? 'text-amber-700' : 'text-gray-900'}`}>{cantConfirmada}</td>
                            <td className="py-1.5 text-right font-bold">
                              {hasDiff ? (
                                <span className={diff > 0 ? 'text-green-600' : 'text-red-600'}>
                                  {diff > 0 ? `+${diff}` : diff}
                                </span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="py-1.5 text-right text-gray-400">{item.peso || 0}</td>
                            <td className="py-1.5 text-right font-semibold text-gray-700">{fmtNum(pesoTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {(isRecol && (loadingEvidencia[tx._id] || evidenciaUrls[tx._id]?.length > 0)) && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide flex items-center gap-1">
                        <Image className="h-3.5 w-3.5" />
                        Evidencias fotográficas
                      </p>
                      {loadingEvidencia[tx._id] ? (
                        <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cargando evidencias...
                        </div>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          {evidenciaUrls[tx._id].map((url, idx) => (
                            <button
                              key={idx}
                              onClick={() => window.open(url, '_blank')}
                              className="relative rounded-lg overflow-hidden border border-gray-200 hover:border-purple-400 hover:shadow-md transition-all"
                            >
                              <img
                                src={url}
                                alt={`Evidencia ${idx + 1}`}
                                className="w-16 h-16 object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ),
          ];
        })}
        {(() => {
          let grandTotalAlistado = 0;
          let grandTotalConfirmado = 0;
          let grandTotalPeso = 0;
          sorted.filter((tx) => !excludedIds.has(tx._id)).forEach((tx) => {
            const items = tx.items || [];
            grandTotalAlistado += items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
            grandTotalConfirmado += items.reduce((s, i) => s + (i.cantidad_confirmada ?? i.cantidad_alistada ?? 0), 0);
            grandTotalPeso += items.reduce((s, i) => {
              const cant = i.cantidad_confirmada ?? i.cantidad_alistada ?? 0;
              return s + (cant * (i.peso || 0) / 1000);
            }, 0);
          });
          return (
            <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold">
              <td />
              <td />
              <td className="px-3 py-2.5 text-gray-900" colSpan={6}>TOTAL ({sorted.length - excludedIds.size} de {sorted.length} registros)</td>
              <td className="px-3 py-2.5 text-right text-gray-900">{grandTotalAlistado}</td>
              <td className="px-3 py-2.5 text-right text-gray-900">{grandTotalConfirmado}</td>
              <td className="px-3 py-2.5 text-right text-gray-900">{fmtNum(grandTotalPeso)}</td>
              <td />
            </tr>
          );
        })()}
      </tbody>
    </table>
  );
};

// ─────────────────────────────────────────────────────────────
// Auto-exclusión: recolecciones consecutivas al final del timeline
// Si la última transacción es una entrega → nada que excluir.
// Si la última es recolección → excluir todas las recolecciones
// del final hasta encontrar una entrega (ropa aún en proceso).
// ─────────────────────────────────────────────────────────────
const computeAutoExcluded = (txs) => {
  const sorted = [...txs].sort(
    (a, b) => new Date(a.fecha_creacion) - new Date(b.fecha_creacion),
  );
  const autoExclude = new Set();
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i].tipo === 'Recoleccion') {
      autoExclude.add(sorted[i]._id);
    } else {
      break; // encontró una entrega → detener
    }
  }
  return autoExclude;
};

// ─────────────────────────────────────────────────────────────
// Informe Panel — mismo dashboard que ve el cliente prendas
// ─────────────────────────────────────────────────────────────
const InformePanel = ({ clienteId, clienteNombre }) => {
  const [dateStart, setDateStart] = useState('2026-01-01');
  const [dateEnd, setDateEnd] = useState(todayStr);
  const [isLoading, setIsLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [kardexFullscreen, setKardexFullscreen] = useState(false);
  const [familiaMap, setFamiliaMap] = useState(new Map());
  const [expandedFamilias, setExpandedFamilias] = useState(new Set());

  const [excludedIds, setExcludedIds] = useState(new Set());

  const toggleFamilia = (name) =>
    setExpandedFamilias((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const toggleExclude = useCallback((id) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setIsLoading(true);
    try {
      const startISO = new Date(dateStart + 'T00:00:00').toISOString();
      const endISO = new Date(dateEnd + 'T23:59:59').toISOString();
      const res = await fetchEntregasClientes({
        query: {
          'cliente.id': clienteId,
          fecha_creacion: { $gte: startISO, $lte: endISO },
        },
        limit: 1000,
        page: 1,
      });
      const txs = res.data || [];
      setTransactions(txs);
      setExcludedIds(computeAutoExcluded(txs));
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [clienteId, dateStart, dateEnd]);

  useEffect(() => {
    loadData();
    getClientGarments({ limit: 500 }).then((res) => {
      const fMap = new Map();
      (res.data || []).forEach((g) => fMap.set(String(g._id), g.familia || null));
      setFamiliaMap(fMap);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const activeTransactions = useMemo(
    () => transactions.filter((t) => !excludedIds.has(t._id)),
    [transactions, excludedIds],
  );

  const recolecciones = useMemo(() => activeTransactions.filter((t) => t.tipo === 'Recoleccion'), [activeTransactions]);
  const entregas = useMemo(() => activeTransactions.filter((t) => t.tipo === 'Entrega'), [activeTransactions]);

  const stats = useMemo(() => {
    let itmRecol = 0, pesoRecol = 0, itmEnt = 0, pesoEnt = 0;
    let totalRecolecciones = 0, totalEntregas = 0;
    activeTransactions.forEach((t) => {
      const isRecol = t.tipo === 'Recoleccion';
      if (isRecol) totalRecolecciones++; else totalEntregas++;
      (t.items || []).forEach((i) => {
        const c = isRecol
          ? (i.cantidad_alistada || 0)
          : (i.cantidad_confirmada ?? 0);
        if (isRecol) { itmRecol += c; pesoRecol += c * (i.peso || 0) / 1000; }
        else { itmEnt += c; pesoEnt += c * (i.peso || 0) / 1000; }
      });
    });
    return {
      totalEntregas, itmEnt, pesoEnt,
      totalRecolecciones, itmRecol, pesoRecol,
      itmBalance: itmRecol -itmEnt,
      pesoBalance:pesoRecol - pesoEnt,
    };
  }, [activeTransactions]);

  const consolidado = useMemo(() => {
    const map = new Map();
    const ensure = (item) => {
      if (!map.has(item.prendaId)) {
        const familia = familiaMap.get(String(item.prendaId)) || null;
        map.set(item.prendaId, { nombre: item.nombre, peso: item.peso || 0, cantRecol: 0, cantEnt: 0, familia });
      }
      return map.get(item.prendaId);
    };
    recolecciones.forEach((r) => (r.items || []).forEach((i) => { ensure(i).cantRecol += i.cantidad_alistada || 0; }));
    entregas.forEach((e) => (e.items || []).forEach((i) => { ensure(i).cantEnt += i.cantidad_confirmada ?? 0; }));
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [recolecciones, entregas, familiaMap]);

  const consolidadoPorFamilia = useMemo(() => {
    const groups = new Map();
    consolidado.forEach((p) => {
      const key = p.familia?.nombre || 'Sin Familia';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Sin Familia') return 1;
      if (b === 'Sin Familia') return -1;
      return a.localeCompare(b);
    });
  }, [consolidado]);

  const { kardexPrendas, kardexTxs, kardexData } = useMemo(() => {
    const allTx = [...activeTransactions].sort(
      (a, b) => new Date(a.fecha_creacion || a.createdAt) - new Date(b.fecha_creacion || b.createdAt),
    );
    const prendasMap = new Map();
    allTx.forEach((t) =>
      (t.items || []).forEach((i) => {
        if (!prendasMap.has(i.prendaId)) prendasMap.set(i.prendaId, { id: i.prendaId, nombre: i.nombre });
      }),
    );
    const prendas = Array.from(prendasMap.values()).sort((a, b) => a.nombre.localeCompare(b.nombre));
    const balances = {};
    prendas.forEach((p) => { balances[p.id] = 0; });
    const data = {};
    prendas.forEach((p) => { data[p.id] = {}; });
    allTx.forEach((tx) => {
      const isRecol = tx.tipo === 'Recoleccion';
      const itemsMap = {};
      (tx.items || []).forEach((i) => {
        itemsMap[i.prendaId] = isRecol ? (i.cantidad_alistada || 0) : (i.cantidad_confirmada ?? 0);
      });
      prendas.forEach((p) => {
        const qty = itemsMap[p.id] || 0;
        const mov = isRecol ? qty : -qty;
        balances[p.id] += mov;
        data[p.id][tx._id] = { mov, balance: balances[p.id] };
      });
    });
    return { kardexPrendas: prendas, kardexTxs: allTx, kardexData: data };
  }, [activeTransactions]);

  const exportConsolidado = () => {
    const rows = [];
    consolidadoPorFamilia.forEach(([familiaName, prendas]) => {
      prendas.forEach((p) => {
        const saldo = p.cantRecol - p.cantEnt;
        rows.push({
          Familia: familiaName, Prenda: p.nombre, 'Peso Unit (g)': p.peso || 0,
          'Cant Recolecciones': p.cantRecol, 'Peso Recol (kg)': parseFloat(toKg(p.peso, p.cantRecol)),
          'Cant Entregas': p.cantEnt, 'Peso Entregado (kg)': parseFloat(toKg(p.peso, p.cantEnt)),
          Saldo: saldo, 'Peso Saldo (kg)': parseFloat(toKg(p.peso, saldo)),
        });
      });
      const fR = prendas.reduce((s, p) => s + p.cantRecol, 0);
      const fE = prendas.reduce((s, p) => s + p.cantEnt, 0);
      const fPR = prendas.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantRecol)), 0);
      const fPE = prendas.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantEnt)), 0);
      rows.push({ Familia: `SUBTOTAL ${familiaName}`, Prenda: '', 'Peso Unit (g)': '', 'Cant Recolecciones': fR, 'Peso Recol (kg)': parseFloat(fPR.toFixed(2)), 'Cant Entregas': fE, 'Peso Entregado (kg)': parseFloat(fPE.toFixed(2)), Saldo: fR - fE, 'Peso Saldo (kg)': parseFloat((fPR - fPE).toFixed(2)) });
    });
    const totR = consolidado.reduce((s, p) => s + p.cantRecol, 0);
    const totPR = consolidado.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantRecol)), 0);
    const totE = consolidado.reduce((s, p) => s + p.cantEnt, 0);
    const totPE = consolidado.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantEnt)), 0);
    rows.push({ Familia: 'TOTAL GENERAL', Prenda: '', 'Peso Unit (g)': '', 'Cant Recolecciones': totR, 'Peso Recol (kg)': parseFloat(totPR.toFixed(2)), 'Cant Entregas': totE, 'Peso Entregado (kg)': parseFloat(totPE.toFixed(2)), Saldo: totR - totE, 'Peso Saldo (kg)': parseFloat((totPR - totPE).toFixed(2)) });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Consolidado');
    XLSX.writeFile(wb, `consolidado_${clienteNombre || clienteId}_${dateStart}_${dateEnd}.xlsx`);
  };

  const exportDetalle = () => {
    const sorted = [...transactions].sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion));
    const rows = sorted.map((tx) => {
      const isRecol = tx.tipo === 'Recoleccion';
      const items = tx.items || [];
      const totalAlistado = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
      const totalConfirmado = items.reduce((s, i) => s + (i.cantidad_confirmada ?? i.cantidad_alistada ?? 0), 0);
      const totalPeso = items.reduce((s, i) => { const c = i.cantidad_confirmada ?? i.cantidad_alistada ?? 0; return s + (c * (i.peso || 0) / 1000); }, 0);
      const fecha = isRecol ? tx.fecha_recoleccion : tx.fecha_entrega;
      return { Excluido: excludedIds.has(tx._id) ? 'Sí' : 'No', Tipo: isRecol ? 'Recolección' : 'Entrega', 'Tipo Entrega': (isRecol ? tx.tipoRecoleccion : tx.tipoEntrega) || '-', Consecutivo: tx.consecutivo, Fecha: fmtDT(fecha || tx.fecha_creacion), Conductor: tx.conductor?.nombre || '-', Receptor: tx.recibe?.nombre || '-', 'Total Alistado': totalAlistado, 'Total Confirmado': totalConfirmado, 'Peso (KG)': parseFloat(totalPeso.toFixed(2)), Diferencias: hasDiferencias(tx) ? 'Sí' : 'No' };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
    XLSX.writeFile(wb, `detalle_${clienteNombre || clienteId}_${dateStart}_${dateEnd}.xlsx`);
  };

  const exportKardex = () => {
    const fmtCol = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      return `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
    };
    const headerRow = ['Prenda', ...kardexTxs.map((tx) => `${tx.tipo === 'Recoleccion' ? 'R' : 'E'} ${tx.consecutivo} (${fmtCol(tx.fecha_recoleccion || tx.fecha_entrega || tx.fecha_creacion)})`), 'Saldo Final'];
    const rows = [headerRow];
    kardexPrendas.forEach((p) => {
      const row = [p.nombre];
      kardexTxs.forEach((tx) => {
        const cell = kardexData[p.id]?.[tx._id];
        if (!cell || cell.mov === 0) {
          row.push('');
        } else {
          const isPos = cell.mov > 0;
          row.push(isPos ? `+${cell.mov}` : String(cell.mov));
        }
      });
      const lastTx = kardexTxs[kardexTxs.length - 1];
      const saldoFinal = lastTx ? (kardexData[p.id]?.[lastTx._id]?.balance ?? 0) : 0;
      row.push(saldoFinal > 0 ? `+${saldoFinal}` : saldoFinal < 0 ? String(saldoFinal) : '');
      rows.push(row);
    });
    const totalsRow = ['TOTAL'];
    kardexTxs.forEach((tx) => {
      const total = kardexPrendas.reduce((s, p) => s + Math.abs(kardexData[p.id]?.[tx._id]?.mov ?? 0), 0);
      const isRecol = tx.tipo === 'Recoleccion';
      totalsRow.push(total > 0 ? (isRecol ? `+${total}` : `-${total}`) : '');
    });
    const totalSaldo = kardexPrendas.reduce((sum, p) => {
      const lastTx = kardexTxs[kardexTxs.length - 1];
      return sum + (lastTx ? (kardexData[p.id]?.[lastTx._id]?.balance ?? 0) : 0);
    }, 0);
    totalsRow.push(totalSaldo > 0 ? `+${totalSaldo}` : totalSaldo < 0 ? String(totalSaldo) : '');
    rows.push(totalsRow);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kardex');
    XLSX.writeFile(wb, `kardex_${clienteNombre || clienteId}_${dateStart}_${dateEnd}.xlsx`);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          Informe de Entregas(limpio) y Recolecciones(sucio)
        </CardTitle>
        <CardDescription>{clienteNombre}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filtros */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Fecha Inicio</label>
            <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Fecha Fin</label>
            <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <Button onClick={loadData} disabled={isLoading} className="bg-gray-900 hover:bg-gray-700 h-9 text-sm">
            {isLoading ? <><RefreshCw className="h-4 w-4 animate-spin mr-2" />Cargando...</> : 'Generar Reporte'}
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
            <p className="text-sm text-gray-400 mt-2">Generando reporte...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">Sin movimientos en el período seleccionado</div>
        ) : (
          <>
            {/* Recolecciones auto-excluidas */}
            {(() => {
              const excludedRecols = transactions.filter(
                (t) => t.tipo === 'Recoleccion' && excludedIds.has(t._id),
              ).sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion));
              if (excludedRecols.length === 0) return null;
              return (
                <div className="flex flex-wrap items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                  <span className="font-semibold shrink-0">Excluidas del informe (en proceso de lavado):</span>
                  <div className="flex flex-wrap gap-1.5">
                    {excludedRecols.map((tx) => {
                      const totalItems = (tx.items || []).reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
                      return (
                        <button
                          key={tx._id}
                          onClick={() => toggleExclude(tx._id)}
                          title="Clic para incluir en el informe"
                          className="inline-flex items-center gap-1 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-full px-2 py-0.5 transition-colors"
                        >
                          <span className="font-bold">{tx.consecutivo}</span>
                          <span className="text-amber-600">·</span>
                          <span>{fmtDT(tx.fecha_recoleccion || tx.fecha_creacion).slice(0, 10).split('-').reverse().join('/')}</span>
                          <span className="text-amber-600">·</span>
                          <span>{totalItems} prendas</span>
                          <span className="ml-0.5 text-amber-500 font-bold">×</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 font-medium">Recolecciones ropa sucia</p>
                <p className="text-2xl font-bold mt-0.5 text-blue-600">{stats.itmRecol.toLocaleString()} <span className="text-sm font-normal text-blue-400">prendas</span></p>
                <p className="text-xs text-gray-400 mt-1">{stats.totalRecolecciones} recolección{stats.totalRecolecciones !== 1 ? 'es' : ''}</p>
                <p className="text-xs text-gray-400">{fmtNum(stats.pesoRecol)} kg</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 font-medium">Entregas ropa limpia</p>
                <p className="text-2xl font-bold mt-0.5 text-green-600">{stats.itmEnt.toLocaleString()} <span className="text-sm font-normal text-green-400">prendas</span></p>
                <p className="text-xs text-gray-400 mt-1">{stats.totalEntregas} entrega{stats.totalEntregas !== 1 ? 's' : ''}</p>
                <p className="text-xs text-gray-400">{fmtNum(stats.pesoEnt)} kg</p>
              </div>
              <div className={`rounded-xl p-4 ${stats.itmBalance > 0 ? 'bg-blue-50' : stats.itmBalance < 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <p className="text-xs text-gray-500 font-medium">Balance Neto</p>
                <p className={`text-2xl font-bold mt-0.5 ${stats.itmBalance > 0 ? 'text-blue-600' : stats.itmBalance < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {stats.itmBalance > 0 ? '+' : ''}{stats.itmBalance.toLocaleString()} <span className="text-sm font-normal text-gray-400">prendas</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">{fmtNum(stats.pesoBalance)} kg</p>
                {stats.itmBalance > 0 && (
                  <p className="text-xs font-medium text-blue-600 mt-1.5">Pendientes por entregar lavandería</p>
                )}
                {stats.itmBalance < 0 && (
                  <p className="text-xs font-medium text-red-600 mt-1.5">De más entregadas al cliente</p>
                )}
              </div>
              <div className={`rounded-xl p-4 ${stats.itmEnt >= stats.itmRecol ? 'bg-green-50' : 'bg-amber-50'}`}>
                <p className="text-xs text-gray-500 font-medium">Cumplimiento Entrega/Recolección</p>
                <p className={`text-2xl font-bold mt-0.5 ${stats.itmEnt >= stats.itmRecol ? 'text-green-600' : 'text-amber-600'}`}>
                  {stats.itmRecol > 0 ? Math.round((stats.itmEnt / stats.itmRecol) * 100) : 0}<span className="text-sm font-normal text-gray-400">%</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">{stats.itmEnt.toLocaleString()} de {stats.itmRecol.toLocaleString()} prendas</p>
                <div className={`text-xs font-medium mt-1.5 flex items-center gap-1 ${stats.itmEnt >= stats.itmRecol ? 'text-green-600' : 'text-amber-600'}`}>
                  <CheckCircle className="h-3 w-3" />
                  {stats.itmEnt >= stats.itmRecol ? 'Cumplimiento total' : 'Pendiente de entrega'}
                </div>
              </div>
            </div>

            {/* Consolidado colapsable por familia */}
            {consolidado.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Consolidado de Prendas</p>
                  <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800" onClick={exportConsolidado}>
                    <Download className="h-3.5 w-3.5" /> Exportar XLSX
                  </Button>
                </div>
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase border-b">
                        <th className="w-8" />
                        <th className="text-left px-3 py-2.5 font-medium">Familia / Prenda</th>
                        <th className="text-right px-3 py-2.5 font-medium">Cant. Recol. (Sucio)</th>
                        <th className="text-right px-3 py-2.5 font-medium">Peso Recol (kg)</th>
                        <th className="text-right px-3 py-2.5 font-medium">Cant. Entregas (Limpio)</th>
                        <th className="text-right px-3 py-2.5 font-medium">Peso Ent. (kg)</th>
                        <th className="text-right px-3 py-2.5 font-medium">Saldo</th>
                        <th className="text-right px-3 py-2.5 font-medium">Peso Saldo (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consolidadoPorFamilia.map(([familiaName, prendas]) => {
                        const isOpen = expandedFamilias.has(familiaName);
                        const fRecol = prendas.reduce((s, p) => s + p.cantRecol, 0);
                        const fEnt = prendas.reduce((s, p) => s + p.cantEnt, 0);
                        const fPesoRecol = prendas.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantRecol)), 0);
                        const fPesoEnt = prendas.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantEnt)), 0);
                        const fSaldo = fRecol - fEnt;
                        const fPesoSaldo = fPesoRecol - fPesoEnt;
                        return (
                          <>
                            <tr
                              key={`fam-${familiaName}`}
                              onClick={() => toggleFamilia(familiaName)}
                              className="border-b bg-gray-100 hover:bg-gray-200 cursor-pointer select-none transition-colors"
                            >
                              <td className="pl-3 py-2.5 w-8">
                                {isOpen
                                  ? <ChevronDown className="h-4 w-4 text-gray-500" />
                                  : <ChevronRight className="h-4 w-4 text-gray-500" />}
                              </td>
                              <td className="px-3 py-2.5 font-bold text-gray-700 text-xs uppercase tracking-wide">
                                {familiaName}
                                <span className="ml-2 font-normal text-gray-400 normal-case tracking-normal">
                                  ({prendas.length} prenda{prendas.length !== 1 ? 's' : ''})
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-right font-bold text-gray-700">{fRecol}</td>
                              <td className="px-3 py-2.5 text-right text-gray-500 text-xs">{fmtNum(fPesoRecol)}</td>
                              <td className="px-3 py-2.5 text-right font-bold text-gray-700">{fEnt}</td>
                              <td className="px-3 py-2.5 text-right text-gray-500 text-xs">{fmtNum(fPesoEnt)}</td>
                              <td className={`px-3 py-2.5 text-right font-bold ${fSaldo < 0 ? 'text-red-600' : fSaldo > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                                {fSaldo < 0 ? fSaldo : fSaldo > 0 ? `+${fSaldo}` : '-'}
                              </td>
                              <td className={`px-3 py-2.5 text-right text-xs font-semibold ${fPesoSaldo < 0 ? 'text-red-500' : fPesoSaldo > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                                {fPesoSaldo === 0 ? '-' : `${fPesoSaldo < 0 ? '' : '+'}${fmtNum(fPesoSaldo)}`}
                              </td>
                            </tr>
                            {isOpen && prendas.map((p, i) => {
                              const saldo = p.cantRecol - p.cantEnt;
                              const pesoSaldo = parseFloat(toKg(p.peso, saldo));
                              return (
                                <tr key={`${familiaName}-${i}`} className="border-b hover:bg-gray-50 transition-colors bg-white">
                                  <td className="py-2" />
                                  <td className="px-3 py-2 pl-6 text-gray-700 font-medium">{p.nombre}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{p.cantRecol}</td>
                                  <td className="px-3 py-2 text-right text-gray-400 text-xs">{toKg(p.peso, p.cantRecol)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{p.cantEnt}</td>
                                  <td className="px-3 py-2 text-right text-gray-400 text-xs">{toKg(p.peso, p.cantEnt)}</td>
                                  <td className={`px-3 py-2 text-right font-semibold ${saldo < 0 ? 'text-red-600' : saldo > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                                    {saldo < 0 ? saldo : saldo > 0 ? `+${saldo}` : '-'}
                                  </td>
                                  <td className={`px-3 py-2 text-right text-xs ${pesoSaldo < 0 ? 'text-red-500' : pesoSaldo > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                                    {pesoSaldo === 0 ? '-' : `${pesoSaldo < 0 ? '' : '+'}${fmtNum(pesoSaldo)}`}
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        );
                      })}
                      {(() => {
                        const totRecol = consolidado.reduce((s, p) => s + p.cantRecol, 0);
                        const totPesoRecol = consolidado.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantRecol)), 0);
                        const totEnt = consolidado.reduce((s, p) => s + p.cantEnt, 0);
                        const totPesoEnt = consolidado.reduce((s, p) => s + parseFloat(toKg(p.peso, p.cantEnt)), 0);
                        const totSaldo = totRecol - totEnt;
                        const totPesoSaldo = totPesoRecol - totPesoEnt;
                        return (
                          <tr className="border-t-2 border-gray-300 bg-gray-200 font-bold">
                            <td className="py-2.5" />
                            <td className="px-3 py-2.5 text-gray-900 text-sm">TOTAL GENERAL</td>
                            <td className="px-3 py-2.5 text-right text-gray-800">{totRecol}</td>
                            <td className="px-3 py-2.5 text-right text-gray-600 text-xs">{fmtNum(totPesoRecol)}</td>
                            <td className="px-3 py-2.5 text-right text-gray-800">{totEnt}</td>
                            <td className="px-3 py-2.5 text-right text-gray-600 text-xs">{fmtNum(totPesoEnt)}</td>
                            <td className={`px-3 py-2.5 text-right ${totSaldo < 0 ? 'text-red-600' : totSaldo > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                              {totSaldo < 0 ? totSaldo : totSaldo > 0 ? `+${totSaldo}` : '-'}
                            </td>
                            <td className={`px-3 py-2.5 text-right text-xs ${totPesoSaldo < 0 ? 'text-red-500' : totPesoSaldo > 0 ? 'text-gray-700' : 'text-gray-400'}`}>
                              {totPesoSaldo === 0 ? '-' : `${totPesoSaldo < 0 ? '' : '+'}${fmtNum(totPesoSaldo)}`}
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Kardex */}
            {kardexTxs.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Kardex Detallado por Transacción</p>
                    <p className="text-xs text-gray-400">Valores (+) recolecciones · (-) entregas</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800" onClick={exportKardex}>
                      <Download className="h-3.5 w-3.5" /> Exportar XLSX
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 shrink-0" onClick={() => setKardexFullscreen(true)}>
                      <Maximize2 className="h-3.5 w-3.5" /> Pantalla completa
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border overflow-hidden">
                  <KardexTableInforme prendas={kardexPrendas} txs={kardexTxs} data={kardexData} />
                </div>
              </div>
            )}

            {/* Detalle */}
            {transactions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Detalle de Entregas y Recolecciones</p>
                    <p className="text-xs text-gray-400">Marca la casilla de una fila para excluirla del consolidado y del kardex</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {excludedIds.size > 0 && (
                      <button
                        onClick={() => setExcludedIds(new Set())}
                        className="text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Limpiar exclusiones ({excludedIds.size})
                      </button>
                    )}
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 border-green-600 text-green-700 hover:bg-green-50 hover:text-green-800" onClick={exportDetalle}>
                      <Download className="h-3.5 w-3.5" /> Exportar XLSX
                    </Button>
                  </div>
                </div>
                {excludedIds.size > 0 && (
                  <div className="mb-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 font-medium">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {excludedIds.size} transacción(es) excluida(s) — el consolidado, kardex y estadísticas no las incluyen
                  </div>
                )}
                <div className="rounded-lg border overflow-x-auto">
                  <DetalleTableInforme
                    transactions={transactions}
                    excludedIds={excludedIds}
                    onToggleExclude={toggleExclude}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* Kardex fullscreen modal */}
        <Dialog open={kardexFullscreen} onOpenChange={setKardexFullscreen}>
          <DialogContent className="!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !max-w-none !w-full !h-full !rounded-none !p-0 !gap-0 !border-0 flex flex-col [&>button]:hidden">
            <DialogTitle className="sr-only">Kardex detallado</DialogTitle>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-white shrink-0">
              <div>
                <p className="font-semibold text-gray-900">Kardex Detallado por Transacción</p>
                <p className="text-xs text-gray-400">Valores (+) recolecciones · (-) entregas</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setKardexFullscreen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <KardexTableInforme prendas={kardexPrendas} txs={kardexTxs} data={kardexData} fullscreen />
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default InformePanel;
